import { ORPCError } from '@orpc/client';
import { rpcClient } from '@/api/rpc/client';
import type {
  FeedbackSupplier,
  HubbellFeedbackRun,
  HubbellFeedbackStagedFile,
} from '@erp/rpc';
import {
  stagedAt,
  stageWorkbooks,
  type UploadStartPhase,
} from './stageWorkbooks';

/** Where an upload-and-start has got to; see {@link UploadStartPhase}. */
export type HubbellFeedbackStartPhase = UploadStartPhase;

export interface HubbellFeedbackDrop {
  /** Whose results the run reads. */
  supplier: FeedbackSupplier;
  resultsWorkbook: File;
  /**
   * The key FINAL.xlsx was staged under by an earlier start, when it is still
   * the same file: it is not uploaded again. The server refuses a key that
   * is gone with `STAGED_FILE_MISSING`; see {@link isStagedFileMissing}.
   */
  resultsUploadKey?: string;
  salesFiles: File[];
  /** Tab letters of the Divisions to build with no sales file (Sales 0). */
  noSalesFileTabs: string[];
}

export interface HubbellFeedbackStarted {
  run: HubbellFeedbackRun;
  /** The key FINAL.xlsx is staged under, to reuse on the next start. */
  resultsUploadKey: string;
}

/** Whether a start failed because a reused staged file no longer exists. */
export function isStagedFileMissing(error: unknown): boolean {
  return (
    error instanceof ORPCError &&
    error.code === 'BAD_REQUEST' &&
    typeof error.data === 'object' &&
    error.data !== null &&
    (error.data as { code?: unknown }).code === 'STAGED_FILE_MISSING'
  );
}

/**
 * Stage the drop in blob storage and start the run.
 *
 * Each workbook goes straight to storage: `getUploadUrl` mints a presigned
 * URL, the file is PUT there raw (an XHR, so there is a percentage), and
 * `start` is given the staged keys with the original names. Nothing binary
 * passes through the API server, and the browser never reads the workbooks.
 * Files go one at a time so the bar means one thing: bytes sent over bytes
 * to send.
 */
export async function startHubbellFeedbackRun(
  drop: HubbellFeedbackDrop,
  onPhase: (phase: HubbellFeedbackStartPhase) => void,
): Promise<HubbellFeedbackStarted> {
  const reuseResults = drop.resultsUploadKey !== undefined;
  const staged = await stageWorkbooks(
    [...(reuseResults ? [] : [drop.resultsWorkbook]), ...drop.salesFiles],
    rpcClient.pricing.hubbellFeedback.getUploadUrl,
    onPhase,
  );
  const resultsWorkbook: HubbellFeedbackStagedFile =
    drop.resultsUploadKey === undefined
      ? stagedAt(staged, 0)
      : {
          uploadKey: drop.resultsUploadKey,
          fileName: drop.resultsWorkbook.name,
        };
  const salesFiles = staged.slice(reuseResults ? 0 : 1);
  onPhase({ phase: 'starting' });
  const run = await rpcClient.pricing.hubbellFeedback.start({
    supplier: drop.supplier,
    resultsWorkbook,
    salesFiles,
    noSalesFileTabs: drop.noSalesFileTabs,
  });
  return { run, resultsUploadKey: resultsWorkbook.uploadKey };
}
